import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { employeeKeys } from "./employee";

export const departmentsKeys = {
  all: (idCompanie: number) => ["departments", "companie", idCompanie] as const,
};

export const useGetAllDepartments = (fleedId: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: departmentsKeys.all(fleedId),
    queryFn: async (): Promise<TDepartment[]> => {
      const data = await axios.get(`/companies/${fleedId}/departments`);
      // GET /companies/{id}/departments answers with a bare array today, but
      // several sibling endpoints wrap their payload ({ department: ... } on
      // create/update here, { vehicule: ... } elsewhere). Every consumer maps
      // over this, so a wrapped or empty body would throw inside render rather
      // than degrade to an empty select.
      const payload = data?.data;
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.departments)) return payload.departments;
      return [];
    },
    enabled: !!fleedId,
  });

  return { departments: data ?? [], ...res };
};

export interface RequestAssignDepartmentCollab {
  department_id: number;
}

export const useAssignDepartementCollab = (
  companyId: number,
  userId: number
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newDepartment: RequestAssignDepartmentCollab) => {
      if (!userId) {
        throw new Error("User ID is required");
      }
      const data = await axios.put(
        `/companies/${companyId}/employees/${userId}/update-department`,
        newDepartment
      );
      return data.data;
    },
    onSuccess: (_) => {
      const keys = employeeKeys.employeeByID(companyId, userId);
      queryClient.refetchQueries({ queryKey: keys });
      toast.success(t("common.changeSaved", "Change saved"));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};

/* ────────────────────────────────────────────────────────────────────────
 * Department (a.k.a. "Service") management — used by the Settings page.
 * Backend (gate: ONLY_SUPER_ADMIN_OR_FLEET_ADMIN):
 *   POST   /companies/{companyId}/departments                 { name }
 *   PUT    /companies/{companyId}/departments/{departmentId}   { name }
 *   DELETE /companies/{companyId}/departments/{departmentId}
 * ──────────────────────────────────────────────────────────────────────── */

export interface RequestDepartmentName {
  name: string;
}

export const useCreateDepartment = (
  companyId: number,
  onSuccess?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RequestDepartmentName): Promise<TDepartment> => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }
      const { data } = await axios.post(
        `/companies/${companyId}/departments`,
        payload
      );
      // Controller returns { department: {...} }
      return data?.department ?? data;
    },
    onSuccess: (department) => {
      const key = departmentsKeys.all(companyId);
      const prev = queryClient.getQueryData<TDepartment[]>(key);
      if (prev && department) {
        queryClient.setQueryData<TDepartment[]>(key, [...prev, department]);
      } else {
        queryClient.invalidateQueries({ queryKey: key });
      }
      onSuccess && onSuccess();
      toast.success(t("setting.serviceAdded", "Service added"));
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("common.errorGlobal", "An error occurred")
      );
    },
  });
};

export const useUpdateDepartment = (
  companyId: number,
  onSuccess?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      departmentId: number;
      name: string;
    }): Promise<TDepartment> => {
      const { data } = await axios.put(
        `/companies/${companyId}/departments/${payload.departmentId}`,
        { name: payload.name }
      );
      return data?.department ?? data;
    },
    onSuccess: (_data, variables) => {
      const key = departmentsKeys.all(companyId);
      const prev = queryClient.getQueryData<TDepartment[]>(key);
      if (prev) {
        queryClient.setQueryData<TDepartment[]>(
          key,
          prev.map((d) =>
            d.id === variables.departmentId ? { ...d, name: variables.name } : d
          )
        );
      } else {
        queryClient.invalidateQueries({ queryKey: key });
      }
      onSuccess && onSuccess();
      toast.success(t("common.changeSaved", "Change saved"));
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("common.errorGlobal", "An error occurred")
      );
    },
  });
};

export const useDeleteDepartment = (
  companyId: number,
  onSuccess?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (departmentId: number) => {
      const { data } = await axios.delete(
        `/companies/${companyId}/departments/${departmentId}`
      );
      return data;
    },
    onSuccess: (_data, departmentId) => {
      // Optimistic removal from the cached list (mirrors useDeleteEmployee).
      const key = departmentsKeys.all(companyId);
      const prev = queryClient.getQueryData<TDepartment[]>(key);
      if (prev) {
        queryClient.setQueryData<TDepartment[]>(
          key,
          prev.filter((d) => d.id !== departmentId)
        );
      }
      onSuccess && onSuccess();
      toast.success(t("setting.serviceDeleted", "Service deleted"));
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("common.errorGlobal", "An error occurred")
      );
    },
  });
};
